import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Spin, Alert, Empty, Button, Select, Input, Typography,
  Space, Avatar, Badge, Tooltip, Collapse, List, Divider, Tag, Progress, Radio
} from 'antd';
import {
  AudioOutlined, UserOutlined, SearchOutlined, FilterOutlined,
  ReloadOutlined, BarChartOutlined, ClockCircleOutlined, SoundOutlined,
  PlayCircleOutlined, PauseCircleOutlined, DownloadOutlined, CaretRightOutlined,
  FileTextOutlined, GlobalOutlined, FieldTimeOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import Chart from 'react-apexcharts';
import { Link } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const ConvertedAudiosPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audioData, setAudioData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [groupedAudios, setGroupedAudios] = useState([]);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [audioElement, setAudioElement] = useState(null);
  const [audioProgress, setAudioProgress] = useState({});

  // Metrics
  const [metrics, setMetrics] = useState({
    totalAudios: 0,
    totalDuration: 0,
    avgDuration: 0,
    commonLanguage: ''
  });

  // Chart states
  const [audiosByDay, setAudiosByDay] = useState({
    categories: [],
    series: [{
      name: 'Audios Converted',
      data: []
    }]
  });

  const [audiosByLanguage, setAudiosByLanguage] = useState({
    labels: [],
    series: []
  });

  // Add time scale state
  const [timeScale, setTimeScale] = useState('daily'); // 'daily', 'weekly', 'monthly'

  useEffect(() => {
    fetchAudioData();
    
    // Initialize audio element
    const audio = new Audio();
    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', handleAudioEnd);
    setAudioElement(audio);
    
    return () => {
      if (audio) {
        audio.pause();
        audio.removeEventListener('timeupdate', updateProgress);
        audio.removeEventListener('ended', handleAudioEnd);
      }
    };
  }, []);

  useEffect(() => {
    if (audioData.length > 0) {
      calculateMetrics();
      prepareChartData();
      groupAudiosByUser();
    }
  }, [audioData, searchText, filterLanguage, timeScale]);

  const updateProgress = () => {
    if (audioElement && currentlyPlaying) {
      const progress = (audioElement.currentTime / audioElement.duration) * 100;
      setAudioProgress({
        ...audioProgress,
        [currentlyPlaying]: progress
      });
    }
  };

  const handleAudioEnd = () => {
    setCurrentlyPlaying(null);
  };

  const fetchAudioData = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllAudioConverted', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data) {
        // Process audio data
        const processedAudioData = processAudioData(data);
        setAudioData(processedAudioData);
      } else {
        throw new Error('Failed to fetch audio data');
      }
    } catch (err) {
      console.error('Error fetching audio data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processAudioData = (usersData) => {
    let allAudios = [];
    
    usersData.forEach(userData => {
      const { user, audios } = userData;
      
      if (audios && audios.length > 0) {
        audios.forEach(audio => {
          allAudios.push({
            ...audio,
            user_name: user.name || 'Unknown User',
            user_email: user.email || 'No email',
            user_uid: user.uid,
            user_avatar: user.dp_url,
            key: `${audio.audioid}`
          });
        });
      }
    });
    
    return allAudios;
  };

  const calculateMetrics = () => {
    if (audioData.length === 0) return;

    const totalAudios = audioData.length;
    const totalDuration = audioData.reduce((sum, audio) => sum + (audio.duration || 0), 0);
    const avgDuration = totalDuration / totalAudios;
    
    // Find most common language
    const languageCounts = {};
    audioData.forEach(audio => {
      const language = audio.language || 'Unknown';
      languageCounts[language] = (languageCounts[language] || 0) + 1;
    });
    
    let commonLanguage = '';
    let maxCount = 0;
    Object.entries(languageCounts).forEach(([language, count]) => {
      if (count > maxCount) {
        commonLanguage = language;
        maxCount = count;
      }
    });

    setMetrics({
      totalAudios,
      totalDuration,
      avgDuration,
      commonLanguage
    });
  };

  const prepareChartData = () => {
    if (audioData.length === 0) return;

    // Time scale calculations
    let timeScalePeriod, startDate;
    const now = new Date();
    
    switch(timeScale) {
      case 'daily':
        timeScalePeriod = 30; // Last 30 days
        startDate = new Date(now);
        startDate.setDate(now.getDate() - timeScalePeriod);
        break;
      case 'weekly':
        timeScalePeriod = 12; // Last 12 weeks
        startDate = new Date(now);
        startDate.setDate(now.getDate() - (timeScalePeriod * 7));
        break;
      case 'monthly':
      default:
        timeScalePeriod = 6; // Last 6 months
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - timeScalePeriod);
        break;
    }

    // Initialize time periods
    const timePeriods = {};
    
    if (timeScale === 'daily') {
      // Initialize last X days
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        timePeriods[dateStr] = 0;
      }
    } else if (timeScale === 'weekly') {
      // Initialize last X weeks
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + (i * 7));
        const weekNumber = Math.ceil((d.getDate() + d.getDay()) / 7);
        const dateStr = `Week ${weekNumber}`;
        timePeriods[dateStr] = 0;
      }
    } else {
      // Monthly (default)
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setMonth(startDate.getMonth() + i);
        const monthYear = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
        timePeriods[monthYear] = 0;
      }
    }

    // Count audios by time period
    audioData.forEach(audio => {
      try {
        const audioDate = new Date(audio.uploaded_at);
        if (audioDate >= startDate) {
          let dateStr;
          
          if (timeScale === 'daily') {
            dateStr = audioDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } else if (timeScale === 'weekly') {
            const weekNumber = Math.ceil((audioDate.getDate() + audioDate.getDay()) / 7);
            dateStr = `Week ${weekNumber}`;
          } else {
            dateStr = `${audioDate.toLocaleString('default', { month: 'short' })} ${audioDate.getFullYear()}`;
          }
          
          if (timePeriods[dateStr] !== undefined) {
            timePeriods[dateStr]++;
          }
        }
      } catch (err) {
        console.error('Error processing audio date:', err);
      }
    });

    setAudiosByDay({
      categories: Object.keys(timePeriods),
      series: [{
        name: 'Audios Converted',
        data: Object.values(timePeriods)
      }]
    });

    // Audios by language
    const languageCounts = {};
    audioData.forEach(audio => {
      const lang = audio.language || 'Unknown';
      languageCounts[lang] = (languageCounts[lang] || 0) + 1;
    });
    
    setAudiosByLanguage({
      labels: Object.keys(languageCounts),
      series: Object.values(languageCounts)
    });
  };

  const groupAudiosByUser = () => {
    let result = [...audioData];
    
    // Apply search filter
    if (searchText) {
      const lowerSearchText = searchText.toLowerCase();
      result = result.filter(audio => 
        (audio.user_name && audio.user_name.toLowerCase().includes(lowerSearchText)) ||
        (audio.user_email && audio.user_email.toLowerCase().includes(lowerSearchText)) ||
        (audio.audio_name && audio.audio_name.toLowerCase().includes(lowerSearchText))
      );
    }
    
    // Apply language filter
    if (filterLanguage !== 'all') {
      result = result.filter(audio => audio.language === filterLanguage);
    }
    
    // Group by user
    const grouped = result.reduce((acc, audio) => {
      const userId = audio.user_uid;
      if (!acc[userId]) {
        acc[userId] = {
          user: {
            uid: userId,
            name: audio.user_name,
            email: audio.user_email,
            avatar: audio.user_avatar
          },
          audios: []
        };
      }
      acc[userId].audios.push(audio);
      return acc;
    }, {});
    
    // Sort audios by date (newest first) within each user
    Object.values(grouped).forEach(group => {
      group.audios.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
    });
    
    // Convert to array and sort by most recent audio
    const groupedArray = Object.values(grouped).sort((a, b) => {
      const latestA = a.audios[0]?.uploaded_at || 0;
      const latestB = b.audios[0]?.uploaded_at || 0;
      return new Date(latestB) - new Date(latestA);
    });
    
    setGroupedAudios(groupedArray);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleLanguageFilterChange = (value) => {
    setFilterLanguage(value);
  };

  const resetFilters = () => {
    setSearchText('');
    setFilterLanguage('all');
  };

  const getLanguages = () => {
    const languages = new Set(audioData.map(audio => audio.language).filter(Boolean));
    return ['all', ...languages];
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlayAudio = (audioid, url) => {
    if (currentlyPlaying === audioid) {
      audioElement.pause();
      setCurrentlyPlaying(null);
    } else {
      if (currentlyPlaying) {
        audioElement.pause();
      }
      audioElement.src = url;
      audioElement.play();
      setCurrentlyPlaying(audioid);
    }
  };

  // Chart options
  const lineOptions = {
    chart: {
      type: 'line',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    colors: ['var(--primary-color)'],
    xaxis: {
      categories: audiosByDay.categories
    },
    yaxis: {
      title: {
        text: 'Audios'
      }
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy'
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: "vertical",
        shadeIntensity: 0.25,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 0.7,
        stops: [0, 100]
      }
    }
  };

  const donutOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    legend: {
      position: 'bottom'
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)', 'var(--secondary-color)', 'var(--accent-color)', 'var(--warning-color)'],
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 600
            },
            total: {
              show: true,
              label: 'Total',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      }
    }
  };

  const renderAudioItem = (audio) => {
    const formattedDate = new Date(audio.uploaded_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    const formattedTime = new Date(audio.uploaded_at).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    const isPlaying = currentlyPlaying === audio.audioid;
    const progress = audioProgress[audio.audioid] || 0;
    
    const audioNameDisplay = audio.audio_name 
      ? (audio.audio_name.length > 40 ? audio.audio_name.substring(0, 37) + '...' : audio.audio_name) 
      : 'Unnamed Audio';
    
    return (
      <List.Item 
        key={audio.audioid}
        className="audio-item"
        style={{
          padding: '16px',
          borderRadius: '8px',
          backgroundColor: 'var(--background-secondary)',
          marginBottom: '8px',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          border: '1px solid var(--border-color)'
        }}
      >
        <div className="audio-content" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <div className="audio-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div className="audio-main" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div className="audio-icon" style={{ position: 'relative' }}>
                <Avatar 
                  icon={<AudioOutlined />} 
                  style={{ 
                    backgroundColor: isPlaying ? 'var(--accent-color)' : 'var(--primary-color)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                  }} 
                  size="large"
                />
                <Button
                  type="text"
                  shape="circle"
                  icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                  onClick={() => togglePlayAudio(audio.audioid, audio.audio_url)}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </div>
              <div className="audio-details" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="audio-title" style={{ fontWeight: '600', fontSize: '16px', color: 'var(--text-primary)' }}>
                  {audioNameDisplay}
                </div>
                <div className="audio-subtitle" style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>
                  Uploaded on {formattedDate} at {formattedTime}
                </div>
              </div>
            </div>
            
            <div className="audio-actions">
              <Space>
                <a href={audio.audio_url} download target="_blank" rel="noopener noreferrer">
                  <Button 
                    icon={<DownloadOutlined />} 
                    type="default" 
                    size="small"
                    title="Download audio"
                  />
                </a>
                <Tag color="blue" icon={<GlobalOutlined />} style={{ borderRadius: '12px' }}>
                  {audio.language || 'Unknown'}
                </Tag>
                <Tag color="purple" icon={<FieldTimeOutlined />} style={{ borderRadius: '12px' }}>
                  {formatDuration(audio.duration)}
                </Tag>
              </Space>
            </div>
          </div>
          
          {isPlaying && (
            <div className="audio-progress" style={{ marginBottom: '16px' }}>
              <Progress 
                percent={progress} 
                showInfo={false} 
                strokeColor={{
                  '0%': 'var(--primary-color)',
                  '100%': 'var(--accent-color)',
                }} 
                size="small"
                status="active"
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                <span>{formatDuration(audioElement?.currentTime || 0)}</span>
                <span>{formatDuration(audio.duration)}</span>
              </div>
            </div>
          )}
          
          {audio.transcription && (
            <div className="audio-transcription" style={{ 
              marginTop: '12px',
              padding: '12px',
              backgroundColor: 'var(--background-tertiary)',
              borderRadius: '8px',
              position: 'relative'
            }}>
              <div style={{ 
                position: 'absolute', 
                top: '-10px', 
                left: '12px', 
                backgroundColor: 'var(--background-secondary)', 
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <FileTextOutlined /> Transcription
              </div>
              <Paragraph 
                ellipsis={{ 
                  rows: 3, 
                  expandable: true, 
                  symbol: 'more'
                }}
                style={{ 
                  margin: 0,
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--text-secondary)'
                }}
              >
                {audio.transcription}
              </Paragraph>
            </div>
          )}
        </div>
      </List.Item>
    );
  };

  const renderUserPanel = (userGroup, index) => {
    const { user, audios } = userGroup;
    const totalAudios = audios.length;
    const totalDuration = audios.reduce((sum, a) => sum + (a.duration || 0), 0);
    
    // Count languages
    const languages = audios.reduce((acc, audio) => {
      const lang = audio.language || 'Unknown';
      acc[lang] = (acc[lang] || 0) + 1;
      return acc;
    }, {});
    
    const primaryLanguage = Object.entries(languages).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Unknown';
    
    return (
      <Panel 
        key={user.uid || index}
        header={
          <div className="user-panel-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Avatar 
                src={user.avatar} 
                icon={!user.avatar && <UserOutlined />} 
                size="large"
                style={{ backgroundColor: 'var(--primary-color)' }}
              />
              <div className="user-details">
                <div className="user-name" style={{ fontWeight: '600', fontSize: '16px' }}>{user.name || 'Unknown User'}</div>
                <div className="user-email" style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>{user.email || 'No email'}</div>
              </div>
            </div>
            <div className="user-stats" style={{ display: 'flex', gap: '24px' }}>
              <div className="stat-item">
                <span className="stat-label" style={{ marginRight: '8px', color: 'var(--text-tertiary)' }}>Audios:</span>
                <span className="stat-value" style={{ fontWeight: '600' }}>{totalAudios}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ marginRight: '8px', color: 'var(--text-tertiary)' }}>Duration:</span>
                <span className="stat-value" style={{ fontWeight: '600' }}>{formatDuration(totalDuration)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ marginRight: '8px', color: 'var(--text-tertiary)' }}>Primary Language:</span>
                <span className="stat-value" style={{ fontWeight: '600' }}>{primaryLanguage}</span>
              </div>
            </div>
          </div>
        }
      >
        <List
          className="audio-list"
          itemLayout="vertical"
          dataSource={audios}
          renderItem={renderAudioItem}
          pagination={{
            pageSize: 5,
            size: 'small',
            showSizeChanger: false
          }}
        />
        <div className="user-actions" style={{ marginTop: '16px', textAlign: 'right' }}>
          <Link to={`/users?uid=${user.uid}`}>
            <Button type="primary" ghost>
              View User Profile
            </Button>
          </Link>
        </div>
      </Panel>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '2rem' }}>
        <Spin size="large" tip="Loading audio data..." spinning={true} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Data"
        description={error}
        type="error"
        showIcon
        style={{ margin: '2rem' }}
      />
    );
  }

  return (
    <div style={{ padding: '24px', background: 'var(--background-primary)', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3} style={{ color: 'var(--primary-color)', margin: 0 }}>
          <AudioOutlined /> Converted Audios
        </Title>
        <Text type="secondary">
          Manage and analyze audio conversions by users
        </Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card primary">
            <div>
              <div className="stat-value">{metrics.totalAudios}</div>
              <div className="stat-label">Total Audios</div>
            </div>
            <AudioOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card secondary">
            <div>
              <div className="stat-value">{formatDuration(metrics.totalDuration)}</div>
              <div className="stat-label">Total Duration</div>
            </div>
            <FieldTimeOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card accent">
            <div>
              <div className="stat-value">{formatDuration(metrics.avgDuration)}</div>
              <div className="stat-label">Avg. Duration</div>
            </div>
            <BarChartOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card warning">
            <div>
              <div className="stat-value">{metrics.commonLanguage || 'None'}</div>
              <div className="stat-label">Most Common Language</div>
            </div>
            <GlobalOutlined className="stat-icon" />
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Audio Conversion Trend" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
            extra={
              <Radio.Group 
                value={timeScale}
                onChange={e => setTimeScale(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                <Radio.Button value="daily">Daily</Radio.Button>
                <Radio.Button value="weekly">Weekly</Radio.Button>
                <Radio.Button value="monthly">Monthly</Radio.Button>
              </Radio.Group>
            }
          >
            <div style={{ height: '280px', width: '100%' }}>
              {audiosByDay.series[0].data.length > 0 ? (
                <Chart
                  options={{
                    ...lineOptions,
                    xaxis: {
                      ...lineOptions.xaxis,
                      categories: audiosByDay.categories,
                      labels: {
                        rotate: -45,
                        style: { fontSize: '12px' }
                      }
                    },
                    grid: {
                      padding: {
                        left: 10,
                        right: 10
                      }
                    }
                  }}
                  series={audiosByDay.series}
                  type="line"
                  height="280"
                  width="100%"
                />
              ) : (
                <Empty description="No audio data available" style={{ marginTop: '80px' }} />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Languages Distribution" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            <div style={{ height: '280px', width: '100%' }}>
              {audiosByLanguage.series.some(value => value > 0) ? (
                <Chart
                  options={{
                    ...donutOptions,
                    labels: audiosByLanguage.labels,
                    chart: {
                      ...donutOptions.chart,
                      width: '100%'
                    }
                  }}
                  series={audiosByLanguage.series}
                  type="donut"
                  height="280"
                  width="100%"
                />
              ) : (
                <Empty description="No language data available" style={{ marginTop: '80px' }} />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="dashboard-card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', marginBottom: '16px', gap: '16px', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search by name, email or audio name"
            value={searchText}
            onChange={handleSearch}
            style={{ maxWidth: '300px' }}
            prefix={<SearchOutlined />}
            allowClear
          />
          
          <Space wrap>
            <Select
              value={filterLanguage}
              onChange={handleLanguageFilterChange}
              style={{ width: '180px' }}
              placeholder="Filter by language"
            >
              {getLanguages().map(lang => (
                <Option key={lang} value={lang}>
                  {lang === 'all' ? 'All Languages' : lang}
                </Option>
              ))}
            </Select>
            
            <Button 
              icon={<ReloadOutlined />} 
              onClick={resetFilters}
              type="text"
              title="Reset filters"
            >
              Reset
            </Button>
          </Space>
          
          <div style={{ marginLeft: 'auto' }}>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              onClick={fetchAudioData}
            >
              Refresh
            </Button>
          </div>
        </div>

        {groupedAudios.length > 0 ? (
          <Collapse 
            className="user-audios-collapse" 
            expandIcon={({ isActive }) => isActive ? <CaretRightOutlined rotate={90} /> : <CaretRightOutlined />}
          >
            {groupedAudios.map((group, index) => renderUserPanel(group, index))}
          </Collapse>
        ) : (
          <Empty description="No audios found matching your filters" />
        )}
      </Card>
    </div>
  );
};

export default ConvertedAudiosPage;